import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "@/trpc/client";
import { useToast } from "@/hooks/use-toast";
import { LogEvents, track } from "@/analytics/track";

export function useThreadActions(businessId: string) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const { toast } = useToast();

  const upsert = useMutation(trpc.salesTeams.upsertThread.mutationOptions());
  const remove = useMutation(trpc.salesTeams.deleteThread.mutationOptions());

  const renameThread = async (
    threadId: string,
    newTitle: string,
    currentTitle: string | null | undefined,
  ): Promise<boolean> => {
    const next = newTitle.trim();
    if (!next || next === currentTitle) return false;
    try {
      const full = await qc.fetchQuery(
        trpc.salesTeams.getThread.queryOptions({ businessId, threadId }),
      );
      if (!full) return false;
      await upsert.mutateAsync({
        businessId,
        threadId,
        topicKey: full.topicKey,
        title: next,
        scope: full.scope,
        salesTeamId: full.salesTeamId,
        memberId: full.memberId,
        messages: full.messages.map((m) => ({
          id: m.id,
          role: m.role,
          parts: m.parts,
          createdAt: m.createdAt,
        })),
      });
      qc.invalidateQueries({ queryKey: [["salesTeams", "listThreads"]] });
      qc.invalidateQueries({
        queryKey: trpc.salesTeams.getThread.queryKey({ businessId, threadId }),
      });
      return true;
    } catch {
      toast({ title: "Kunde inte byta namn", variant: "destructive" });
      return false;
    }
  };

  const deleteThread = async (
    threadId: string,
    thread?: { topicKey?: string | null; scope?: string | null },
  ): Promise<void> => {
    try {
      await remove.mutateAsync({ businessId, threadId });
      track({
        event: LogEvents.IntelligenceThreadDeleted.name,
        channel: LogEvents.IntelligenceThreadDeleted.channel,
        topic_key: thread?.topicKey,
        scope: thread?.scope,
      });
      qc.invalidateQueries({ queryKey: [["salesTeams", "listThreads"]] });
      qc.invalidateQueries({
        queryKey: trpc.salesTeams.getThread.queryKey({ businessId, threadId }),
      });
    } catch {
      toast({ title: "Kunde inte ta bort tråden", variant: "destructive" });
    }
  };

  return { renameThread, deleteThread };
}
